import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Row,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router";

interface CreditBillingStatementFormProps {
  add?: boolean;
  edit?: boolean;
  review?: boolean;
  approval?: boolean;
  view?: boolean;
}

function computeTotals(total: number, vatType: string, walletAmount: number) {
  let vatEx: number;
  let vat: number;
  let whTax: number;
  let baseGrandTotal: number;

  switch (vatType) {
    case "vat_in": {
      vatEx          = total / 1.12;
      vat            = total - vatEx;
      whTax          = vatEx * 0.02;
      baseGrandTotal = total - whTax;
      break;
    }
    case "vat_ex": {
      vatEx          = total;
      vat            = total * 0.12;
      whTax          = vatEx * 0.02;
      baseGrandTotal = total + vat - whTax;
      break;
    }
    case "zero_rated":
    default: {
      vatEx          = total;
      vat            = 0;
      whTax          = vatEx * 0.02;
      baseGrandTotal = total - whTax;
      break;
    }
  }

  const walletUsed      = Math.min(walletAmount, baseGrandTotal);
  const grandTotal      = Math.max(0, baseGrandTotal - walletUsed);
  const remainingWallet = Math.max(0, walletAmount - walletUsed);

  return { total, vatEx, vat, whTax, grandTotal, remainingWallet };
}

const CreditBillingStatementForm: React.FC<CreditBillingStatementFormProps> = ({
  add,
  edit,
  review,
  approval,
  view
}) => {
  const [loading,              setLoading]           = useState("");
  const [showReasonModal,      setShowReasonModal]   = useState(false);
  const [cancelReason,         setCancelReason]      = useState("");
  const [companyId,            setCompanyId]         = useState<number | null>(null);
  const [wallet,               setWallet]            = useState<any>(null);
  const [filterConfig,         setFilterConfig]      = useState({
    customer_id: "",
    date_from:  "",
    date_to:    "",
  });
  const [customer,            setCustomer]            = useState<any[]>([]);
  const [dailyTransactionData, setDailyTransactionData] = useState<any[]>([]);
  const [discount, setDiscount] = useState<any[]>([]);
  const [selectedCustomerId,    setSelectedCustomerId]    = useState<number | null>(null);
  const [form]                                          = Form.useForm();

  const { id: credit_billing_id } = useParams<{ id: string }>();

  const isReadOnly = review || approval || view;

  const selectedCompany = customer.find((c: any) => Number(c.id) === selectedCustomerId);
  const isBranch        = selectedCompany?.is_branch == 1;
  const vatType         = selectedCompany?.vat_type ?? "zero_rated";

  function clearFields() {
    form.resetFields();
    setDailyTransactionData([]);
    setDiscount([]);
    setSelectedCustomerId(null);
    setCompanyId(null);
    setWallet(null);
  }

  const rawTotal = dailyTransactionData.reduce((sum: number, item: any) => {
    return sum + (Number(item?.amount ?? item?.remaining_balance) || 0);
  }, 0);

  const totalDiscount = dailyTransactionData.reduce((sum: number, item: any) => {
    return sum + (Number(item?.discount_amount) || 0);
  }, 0);

  const billableTotal = Math.max(0, rawTotal - totalDiscount);

  const { vatEx, vat, whTax, grandTotal, remainingWallet } = computeTotals(
    billableTotal,
    vatType,
    wallet?.available_amount ?? 0
  );

  function attachDiscounts(transactions: any[], discounts: any[]) {
    const discountByTxnId: Record<number, number> = {};

    for (const row of discounts) {
      const txnId = Number(row.daily_transaction_id ?? row.transaction_id);
      if (!txnId) continue;
      if (row.status && String(row.status).toLowerCase() !== "approved") continue;
      discountByTxnId[txnId] =
        (discountByTxnId[txnId] || 0) + (Number(row.amount) || 0);
    }

    return transactions.map((item: any) => {
      const txnId = Number(item.id ?? item.transaction_id);
      const mappedDiscount = discountByTxnId[txnId];
      return {
        ...item,
        discount_amount:
          mappedDiscount !== undefined
            ? mappedDiscount
            : Number(item.discount_amount) || 0,
      };
    });
  }

  async function submitForm(data: any) {
    const formattedItemsArray = dailyTransactionData.map((item: any) => ({
      transaction_id:   item.id ?? item.transaction_id,
      transaction_date: item.transaction_date
        ? dayjs(item.transaction_date).format("YYYY-MM-DD")
        : "",
      ...(isBranch
        ? { test_type: item.test_type, quantity: item.quantity ?? item.sample_count }
        : { rwt_no: item.rwt_no }),
      amount:          Number(item.amount ?? item.remaining_balance) || 0,
      discount_amount: Number(item.discount_amount) || 0,
    }));

    const formattedData: any = {
      ...data,
      date_from:         dayjs(data.date_range[0]).format("YYYY-MM-DD"),
      date_to:           dayjs(data.date_range[1]).format("YYYY-MM-DD"),
      itemsArray:        formattedItemsArray,
      total:             rawTotal,
      discount:          totalDiscount,
      vat_ex:            vatEx,
      vat,
      wh_tax:            whTax,
      _2307_calculation: grandTotal,
      remaining_wallet:  remainingWallet,
      wallet_paid:       Math.max(0, (wallet?.available_amount ?? 0) - remainingWallet),
    };

    if (edit) formattedData.credit_billing_id = credit_billing_id;

    setLoading("submit");
    try {
      const response = await postRequest(
        add ? "credit_billings/create" : "credit_billings/update",
        formattedData,
        true
      );

      if (response.success) {
        message.success(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          navigateTo("/credit-billing-statement");
        }, 1500);
      } else {
        message.error(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            `Failed to ${add ? "create" : "update"} credit billing statement.`
        );
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function approveCancelRequest() {
    setLoading("cancel");
    try {
      const response = await postRequest(
        "credit_billings/change_status",
        { credit_billing_id, action: "approve_cancel_request" },
        true
      );
      if (response.success) {
        message.success(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          navigateTo("/credit-billing-statement");
        }, 1500);
      } else {
        message.error(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to approve cancel request for credit billing statement."
        );
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function cancelRequest() {
    if (!cancelReason.trim()) {
      message.warning("Please provide a reason for cancellation.");
      return;
    }
    setShowReasonModal(false);
    setLoading("cancel");
    try {
      const response = await postRequest(
        "credit_billings/change_status",
        { credit_billing_id, action: "cancel_request", reason: cancelReason },
        true
      );
      if (response.success) {
        message.success(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          navigateTo("/credit-billing-statement");
        }, 1500);
      } else {
        message.error(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to cancel credit billing statement."
        );
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function fetchDiscount(dailyTransactionIds: number[] = []) {
    setDiscount([]);
    const response = await getRequest(
      `discounts/get`,
      { status: "approved" },
      true
    );

    if (!response.success) {
      setDiscount([]);
      return [];
    }

    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    const filtered =
      dailyTransactionIds.length > 0
        ? rows.filter((row: any) =>
            dailyTransactionIds.includes(
              Number(row.daily_transaction_id ?? row.transaction_id)
            )
          )
        : rows;

    setDiscount(filtered);
    return filtered;
  }

  async function fetchCompanies() {
    setCustomer([]);
    const response = await getRequest(`customers/get`, {}, true);
    if (response.success) setCustomer(response.data.data);
  }

  async function fetchWallet(company_id: number) {
    setWallet(null);
    const response = await getRequest(`advance_payments/get`, { company_id }, true);
    if (response.success) {
      const data = response.data.data;
      setWallet(Array.isArray(data) ? (data[0] ?? null) : (data ?? null));
    }
  }

  async function fetchUnpaidBillings() {
    setDailyTransactionData([]);
    setDiscount([]);
    const response = await getRequest(
      `daily_transactions/get`,
      { status: "pending", ...filterConfig },
      true
    );
    if (!response.success) {
      setDailyTransactionData([]);
      return;
    }

    const transactions = Array.isArray(response.data?.data)
      ? response.data.data
      : [];
    const txnIds = transactions
      .map((item: any) => Number(item.id))
      .filter((id: number) => !!id);
    const discounts = await fetchDiscount(txnIds);
    setDailyTransactionData(attachDiscounts(transactions, discounts));
  }

  async function fetchCreditBillingData() {
    if (!credit_billing_id) return;
    setLoading("fetch");
    const response = await getRequest("credit_billings/get", { credit_billing_id, ...filterConfig }, true);
    if (response.success) {
      const data = response.data.data[0];

      const baseFields: any = {
        customer_id:  Number(data.customer_id),
        statement_no: data.statement_no,
        date_range:   [
          data.date_from ? dayjs(data.date_from) : null,
          data.date_to   ? dayjs(data.date_to)   : null,
        ],
      };

      setSelectedCustomerId(Number(data.customer_id));
      setCompanyId(Number(data.company_id));

      if (data.items && data.items.length > 0) {
        const transactions = data.items;
        const txnIds = transactions
          .map((item: any) => Number(item.id ?? item.transaction_id ?? item.daily_transaction_id))
          .filter((id: number) => !!id);
        const discounts = await fetchDiscount(txnIds);
        setDailyTransactionData(attachDiscounts(transactions, discounts));
      } else {
        setDailyTransactionData([]);
        setDiscount([]);
      }

      form.setFieldsValue(baseFields);
    }
    setLoading("");
  }

  useEffect(() => {
    (async () => {
      await fetchCompanies();
      if (edit || review || approval || view) await fetchCreditBillingData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only load unpaid billings when creating/editing — not on view/review/approval
    if ((add || edit) && filterConfig.customer_id && filterConfig.date_from && filterConfig.date_to) {
      fetchUnpaidBillings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  useEffect(() => {
    if (companyId) fetchWallet(companyId);
    else setWallet(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const pageTitle = add      ? "Add Credit Billing Statement"
    : edit     ? "Edit Credit Billing Statement"
    : review   ? "Review Credit Billing Statement"
    : approval ? "Approve Cancel Credit Billing Statement"
    : view     ? "View Credit Billing Statement"
    :            "Credit Billing Statement";

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader title={pageTitle} withDateRangeFilter={false} withAddButton={false} withSearchBtn={false} />
          </Col>

          {loading !== "fetch" && (
            <div className="form-container w-full">
              <Col span={24}>
                <Form form={form} onFinish={submitForm} layout="vertical">
                  <Row className="w-full" gutter={[10, 10]}>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Company Name" name="customer_id"
                        rules={[{ required: true, message: "This field is required." }]}>
                        <Select
                          placeholder="Select company"
                          showSearch
                          allowClear
                          onChange={(value) => {
                            const id = value ? Number(value) : null;
                            const selected = customer.find((c: any) => Number(c.id) === id);
                            setCompanyId(selected ? Number(selected.company_id) : null);
                            setSelectedCustomerId(id);
                            setFilterConfig((prev) => ({ ...prev, customer_id: value ?? "" }));
                            if (!value) setWallet(null);
                          }}
                          optionFilterProp="label"
                          disabled={isReadOnly || edit}
                          options={customer.map((info: any) => ({
                            label: info.branch_company,
                            value: Number(info.id),
                          }))}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Billing Statement Number" name="statement_no"
                        rules={[{ required: true, message: "This field is required." }]}>
                        <Input disabled={isReadOnly} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Date Range"
                        name="date_range"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <DatePicker.RangePicker
                          style={{ width: "100%" }}
                          format="YYYY-MM-DD"
                          placeholder={["Start date", "End date"]}
                          disabled={isReadOnly}
                          onChange={(_, dateStrings) =>
                            setFilterConfig((prev) => ({
                              ...prev,
                              date_from: dateStrings[0],
                              date_to:   dateStrings[1],
                            }))
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <span className="edit-label mb-2">
                        <label className="text-red-500 me-1">*</label>List of Unpaid Billings:
                      </span>

                      <div className="overflow-auto max-h-[600px]">

                        {dailyTransactionData.length > 0 && !isBranch && (
                          <table className="w-full border border-collapse min-w-[700px]">
                            <thead>
                              <tr className="bg-primary text-white font-bold border">
                                <th className="p-2 border w-[50px]">#</th>
                                <th className="p-2 border">Transaction Date</th>
                                <th className="p-2 border">RWT No</th>
                                <th className="p-2 border">Amount</th>
                                <th className="p-2 border">Discount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyTransactionData.map((item: any, index: number) => (
                                <tr key={item.id ?? item.transaction_id ?? index} className="border bg-white">
                                  <td className="p-2 border text-center text-sm text-gray-500">{index + 1}</td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.transaction_date} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.rwt_no} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={formatNumbers(item.amount ?? item.remaining_balance)} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={formatNumbers(item.discount_amount || 0)} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {dailyTransactionData.length > 0 && isBranch && (
                          <table className="w-full border border-collapse min-w-[700px]">
                            <thead>
                              <tr className="bg-primary text-white font-bold border">
                                <th className="p-2 border w-[50px]">#</th>
                                <th className="p-2 border">Test Type</th>
                                <th className="p-2 border">Quantity</th>
                                <th className="p-2 border">Amount</th>
                                <th className="p-2 border">Discount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyTransactionData.map((item: any, index: number) => (
                                <tr key={item.id ?? item.transaction_id ?? index} className="border bg-white">
                                  <td className="p-2 border text-center text-sm text-gray-500">{index + 1}</td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.test_type} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={item.sample_count} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={formatNumbers(item.amount ?? item.remaining_balance)} />
                                  </td>
                                  <td className="p-2 border">
                                    <Input disabled value={formatNumbers(item.discount_amount || 0)} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {dailyTransactionData.length === 0 && (
                          <div className="text-center text-gray-400 py-6 border rounded">
                            {selectedCustomerId
                              ? "No unpaid billings found for this company."
                              : "Select a company to load unpaid billings."}
                          </div>
                        )}

                      </div>

                      <div className="flex flex-col items-end gap-1 mt-3 text-sm">
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">Total:</span>
                          <strong>₱ {formatNumbers(rawTotal)}</strong>
                        </div>
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">Discount:</span>
                          <strong>₱ {formatNumbers(totalDiscount)}</strong>
                        </div>
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">Vat Ex:</span>
                          <strong>₱ {formatNumbers(vatEx)}</strong>
                        </div>
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">
                            Vat {vatType === "zero_rated" ? "(0%)" : "(12%)"}:
                          </span>
                          <strong>₱ {formatNumbers(vat)}</strong>
                        </div>
                        <div className="flex justify-between w-full max-w-xs">
                          <span className="text-gray-500">WHTax (2%):</span>
                          <strong>₱ {formatNumbers(whTax)}</strong>
                        </div>
                        {wallet && add && (
                          <>
                            <div className="flex justify-between w-full max-w-xs">
                              <span className="text-gray-500">Wallet Balance:</span>
                              <strong>₱ {formatNumbers(wallet.available_amount ?? 0)}</strong>
                            </div>
                            <div className="flex justify-between w-full max-w-xs">
                              <span className="text-gray-500">Remaining Wallet:</span>
                              <strong>₱ {formatNumbers(remainingWallet)}</strong>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between w-full max-w-xs border-t pt-1 mt-1">
                          <span className="font-bold">Grand Total:</span>
                          <strong className="text-primary">₱ {formatNumbers(grandTotal)}</strong>
                        </div>
                      </div>
                    </Col>

                    <Col lg={24} className="mt-5 flex justify-end gap-3">
                      <Button onClick={() => { navigateTo("/credit-billing-statement"); clearFields(); }}>
                        {isReadOnly ? "Close" : "Cancel"}
                      </Button>

                      {(add || edit) && (
                        <Button htmlType="submit" type="primary" loading={loading === "submit"} disabled={dailyTransactionData.length === 0}>
                          {add ? "Submit" : "Update"}
                        </Button>
                      )}

                      {review && (
                        <Button
                          danger
                          loading={loading === "cancel"}
                          onClick={() => {
                            setCancelReason("");
                            setShowReasonModal(true);
                          }}
                        >
                          Cancel Request
                        </Button>
                      )}

                      {approval && (
                        <ConfirmAction
                          title="Are you sure you want to approve the cancel request for this credit billing statement?"
                          onConfirm={approveCancelRequest}
                          okText="Approve"
                          cancelText="Cancel"
                          okButtonProps={{ loading: loading === "cancel" }}
                        >
                          <Button type="primary" loading={loading === "cancel"}>
                            Approve
                          </Button>
                        </ConfirmAction>
                      )}
                    </Col>

                  </Row>
                </Form>
              </Col>
            </div>
          )}
        </Row>
      </Layout>

      <ReasonModal
        showModal={showReasonModal}
        onCancel={() => setShowReasonModal(false)}
        title="Please provide a reason for cancellation:"
        reasonValue={cancelReason}
        onReasonChange={(e: any) => setCancelReason(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowReasonModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "cancel"}
            disabled={!cancelReason.trim()}
            onClick={cancelRequest}
          >
            Confirm
          </Button>,
        ]}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default CreditBillingStatementForm;